import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Plus, Calendar, MapPin, Users, ChevronDown, X, Filter } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from './SearchableSelect';
import VerifiedBadge from './VerifiedBadge';
import { ALL_STATES, getDistrictsForState } from './data/indianStatesDistricts';
import { assetUrl, listEvents } from '../apis/ride-24';

const BIKE_TYPE_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'cruiser', label: 'Cruiser' },
  { value: 'sport', label: 'Sport / Supersport' },
  { value: 'touring', label: 'Touring' },
  { value: 'adventure', label: 'Adventure / ADV' },
  { value: 'dirt', label: 'Dirt / Off-road' },
  { value: 'naked', label: 'Naked / Streetfighter' },
  { value: 'cafe_racer', label: 'Cafe Racer' },
  { value: 'scooter', label: 'Scooter' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'challenging', label: 'Challenging' },
  { value: 'expert', label: 'Expert' },
];

// Fallback hero images when a ride has no photos yet — keeps the grid feeling premium.
const FALLBACK_COVERS = [
  'https://images.unsplash.com/photo-1620193565717-cdc48028fc56?w=1200&q=80',
  'https://images.unsplash.com/photo-1597840662268-226a09bc49f7?w=1200&q=80',
  'https://images.unsplash.com/photo-1466095834737-14d0fe2abe94?w=1200&q=80',
];

const hashCode = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const Dashboard = ({ user }) => {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastOpen, setPastOpen] = useState(false);

  const [filters, setFilters] = useState({
    bike_type: null,
    difficulty_level: null,
    state: null,
    district: null,
    month: '',
  });

  const buildParams = useCallback((timeFilter) => {
    const params = { time_filter: timeFilter };
    if (filters.bike_type) params.bike_type = filters.bike_type;
    if (filters.difficulty_level) params.difficulty_level = filters.difficulty_level;
    if (filters.state) params.state = filters.state;
    if (filters.district) params.district = filters.district;
    if (filters.month) params.month = filters.month;
    return params;
  }, [filters]);

  const fetchRides = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        listEvents(buildParams('upcoming')),
        listEvents(buildParams('past')),
      ]);
      setUpcoming(u.data);
      setPast(p.data);
    } catch (error) {
      console.error('Error fetching rides:', error);
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchRides();
  }, [fetchRides]);

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'state') next.district = null;
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({ bike_type: null, difficulty_level: null, state: null, district: null, month: '' });
  };

  const hasAnyFilter =
    filters.bike_type || filters.difficulty_level || filters.state || filters.district || filters.month;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stateOptions = ALL_STATES.map((s) => ({ value: s, label: s }));
  const districtOptions = getDistrictsForState(filters.state).map((d) => ({ value: d, label: d }));

  const RideCard = ({ event, muted = false }) => {
    const cover =
      event.photos && event.photos.length > 0
        ? assetUrl(event.photos[0].path)
        : FALLBACK_COVERS[hashCode(event.id) % FALLBACK_COVERS.length];

      return (
        <Link
          to={`/ride-24/events/${event.id}`}
        className="group block"
        data-testid={`event-card-${event.id}`}
      >
        <article
          className={`relative overflow-hidden rounded-sm border border-zinc-900 bg-zinc-950 transition-all duration-300 hover:border-red-900/60 hover:ring-1 hover:ring-red-900/40 hover:shadow-[0_10px_40px_rgb(0,0,0,0.9)] ${
            muted ? 'opacity-70 grayscale hover:opacity-100 hover:grayscale-0' : ''
          }`}
        >
          {/* Cover image with gradient overlay + bottom-anchored content */}
          <div className="relative h-64 sm:h-72 overflow-hidden">
            <img
              src={cover}
              alt={event.title}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent"></div>

            {/* Difficulty badge — top right */}
            <span
              className={`absolute top-3 right-3 uppercase font-heading tracking-wider text-[10px] px-2.5 py-1 rounded-sm border ${
                muted
                  ? 'bg-zinc-800/90 text-zinc-300 border-zinc-700'
                  : 'bg-red-600/90 text-white border-red-500'
              }`}
            >
              {event.difficulty_level}
            </span>

            {/* Motorcycle category chip — top left */}
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 uppercase font-heading tracking-wider text-[10px] px-2.5 py-1 rounded-sm bg-black/60 backdrop-blur-sm border border-zinc-800 text-zinc-200">
              <i className="fa-solid fa-motorcycle not-italic text-red-500" aria-hidden="true"></i>
              {event.bike_type.replace('_', ' ')}
            </span>

            {/* Gender restriction chip — top left below category */}
            {event.gender_restriction && event.gender_restriction !== 'anyone' && (
              <span
                className={`absolute top-11 left-3 inline-flex items-center gap-1 uppercase font-heading tracking-wider text-[10px] px-2.5 py-1 rounded-sm backdrop-blur-sm border ${
                  event.gender_restriction === 'female_only'
                    ? 'bg-pink-950/70 border-pink-800 text-pink-200'
                    : 'bg-sky-950/70 border-sky-800 text-sky-200'
                }`}
                data-testid={`ride-gender-badge-${event.id}`}
              >
                {event.gender_restriction === 'female_only' ? '♀ Female only' : '♂ Male only'}
              </span>
            )}

            {/* Bottom content */}
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="mb-2 text-xs uppercase tracking-widest text-red-400 font-heading">
                <Calendar className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
                {formatDate(event.date)}
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight leading-tight line-clamp-2 mb-1.5">
                {event.title}
              </h3>
              {(event.district || event.state) && (
                <div className="text-sm text-zinc-300 flex items-center">
                  <MapPin className="w-3.5 h-3.5 mr-1 text-red-500" />
                  {[event.district, event.state].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Bottom meta row */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-zinc-900 bg-zinc-950">
            <div className="flex items-center gap-4 text-xs text-zinc-400 font-body">
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                <span className="text-zinc-200 font-medium">
                  {event.attending_count || 0}
                </span>
                / {event.max_participants}
              </span>
              <span className="truncate max-w-[9rem] inline-flex items-center gap-1">
                by {event.organizer_name}
                {event.organizer_is_verified && <VerifiedBadge size="sm" />}
              </span>
            </div>
            <span
              className="text-xs uppercase font-heading tracking-widest text-zinc-300 group-hover:text-red-400 transition-colors"
              data-testid={`view-event-button-${event.id}`}
            >
              Details →
            </span>
          </div>
        </article>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-heading mb-2">
              Riders&apos; garage
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-white leading-none"
              data-testid="dashboard-title"
            >
              Hello, {user.name.split(' ')[0]}
            </h1>
            <p className="text-zinc-400 mt-3">
              Discover and join motorcycle rides across India.
            </p>
          </div>
          <Link to="/ride-24/create-event">
            <Button
              className="btn-hover bg-red-600 hover:bg-red-700 text-white border-0 rounded-sm px-6 py-6 font-heading uppercase tracking-widest text-sm"
              data-testid="create-event-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Ride
            </Button>
          </Link>
        </div>

        {/* Filter bar */}
        <div className="mb-10" data-testid="filter-bar">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center text-xs uppercase tracking-[0.25em] font-heading text-zinc-400">
              <Filter className="w-3.5 h-3.5 mr-2 text-red-500" />
              Filters
            </div>
            {hasAnyFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors font-heading"
                data-testid="clear-filters-button"
              >
                <X className="inline w-3 h-3 mr-1" />
                Clear all
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-4 no-scrollbar border-b border-zinc-900">
            <div className="min-w-[10rem]">
              <SearchableSelect
                value={filters.bike_type}
                onChange={(v) => updateFilter('bike_type', v)}
                options={BIKE_TYPE_OPTIONS}
                placeholder="All categories"
                testId="filter-bike-type"
                allowClear
                clearLabel="All categories"
              />
            </div>
            <div className="min-w-[9rem]">
              <SearchableSelect
                value={filters.difficulty_level}
                onChange={(v) => updateFilter('difficulty_level', v)}
                options={DIFFICULTY_OPTIONS}
                placeholder="All levels"
                testId="filter-difficulty"
                allowClear
                clearLabel="All levels"
              />
            </div>
            <div className="min-w-[10rem]">
              <SearchableSelect
                value={filters.state}
                onChange={(v) => updateFilter('state', v)}
                options={stateOptions}
                placeholder="All states"
                testId="filter-state"
                allowClear
                clearLabel="All states"
              />
            </div>
            <div className="min-w-[10rem]">
              <SearchableSelect
                value={filters.district}
                onChange={(v) => updateFilter('district', v)}
                options={districtOptions}
                placeholder={filters.state ? 'All districts' : 'Pick state first'}
                testId="filter-district"
                disabled={!filters.state}
                allowClear
                clearLabel="All districts"
              />
            </div>
            <div className="relative min-w-[9.5rem]">
              <input
                type="month"
                value={filters.month}
                onChange={(e) => updateFilter('month', e.target.value)}
                className="w-full h-10 px-3 pr-8 rounded-sm bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 text-zinc-200 placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-red-600 [color-scheme:dark] text-sm"
                data-testid="filter-month-input"
                aria-label="Filter by month"
              />
              {!filters.month && (
                <span
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500 select-none"
                  data-testid="filter-month-placeholder"
                >
                  Month
                </span>
              )}
              {filters.month && (
                <button
                  type="button"
                  onClick={() => updateFilter('month', '')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-red-400"
                  aria-label="Clear month"
                  data-testid="clear-month-button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Rides */}
        <section className="mb-14" data-testid="upcoming-section">
          <div className="flex items-baseline gap-3 mb-6">
            <div className="h-4 w-1 bg-red-600"></div>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white">
              Upcoming rides
            </h2>
            <span className="text-zinc-500 text-sm font-body">({upcoming.length})</span>
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-80 bg-zinc-900 rounded-sm animate-pulse" />
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <Card
              className="text-center py-16 bg-zinc-950 border border-zinc-900 rounded-sm text-zinc-200"
              data-testid="no-upcoming-rides"
            >
              <CardContent>
                <i className="fa-solid fa-motorcycle text-red-500 text-6xl mb-4 block" aria-hidden="true"></i>
                <h3 className="font-heading text-2xl uppercase tracking-tight mb-2 text-white">
                  No upcoming rides
                </h3>
                <p className="text-zinc-500 mb-6">
                  {hasAnyFilter
                    ? 'No rides match your filters. Try loosening them.'
                    : 'Be the first to organize a motorcycle ride for the community!'}
                </p>
                {!hasAnyFilter && (
                  <Link to="/ride-24/create-event">
                    <Button className="btn-hover bg-red-600 hover:bg-red-700 text-white border-0 rounded-sm font-heading uppercase tracking-widest">
                      <Plus className="w-4 h-4 mr-2" />
                      Create first ride
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {upcoming.map((event) => (
                <RideCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </section>

        {/* Past Rides (collapsible) */}
        {past.length > 0 && (
          <Collapsible
            open={pastOpen}
            onOpenChange={setPastOpen}
            data-testid="past-section"
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex justify-between items-center py-5 border-t border-zinc-900 text-zinc-500 hover:text-zinc-200 transition-colors uppercase font-heading tracking-[0.25em] text-sm mb-6 group"
                data-testid="past-section-toggle"
              >
                <span>Past rides · {past.length}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform group-hover:text-red-400 ${
                    pastOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 pb-10">
                {past.map((event) => (
                  <RideCard key={event.id} event={event} muted />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
